<template>
    <div class="p-3">
        <div class="d-flex justify-content-between align-items-center mb-4">
            <h4 class="text-gray-800 font-weight-bold m-0">Mis Cuentas</h4>
            <button class="btn btn-teal btn-sm shadow-sm" @click="openModal()">
                <i class="fas fa-plus mr-1"></i> Nueva Cuenta
            </button>
        </div>
        
        <div class="table-responsive rounded-lg shadow-sm border border-light">
            <table class="table table-hover mb-0 custom-table">
                <thead class="bg-light-gray">
                    <tr>
                        <th class="text-uppercase text-secondary font-weight-bold text-xs border-0 pl-4">Nombre</th>
                        <th class="text-uppercase text-secondary font-weight-bold text-xs border-0">Descripción</th>
                        <th class="text-uppercase text-secondary font-weight-bold text-xs border-0">Link</th>
                        <th class="text-uppercase text-secondary font-weight-bold text-xs border-0">Identificador</th>
                        <th class="text-uppercase text-secondary font-weight-bold text-xs border-0">Contraseña</th>
                        <th class="text-uppercase text-secondary font-weight-bold text-xs border-0 text-right pr-4">Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="account in accounts" :key="account.id">
                        <td class="align-middle pl-4 font-weight-bold text-dark">{{ account.name }}</td>
                        <td class="align-middle text-muted">{{ account.description }}</td>
                        <td class="align-middle">
                            <a v-if="account.access_link" :href="account.access_link" target="_blank" class="text-teal text-decoration-none">
                                <i class="fas fa-external-link-alt mr-1"></i> Abrir
                            </a>
                            <span v-else class="text-muted">-</span>
                        </td>
                        <td class="align-middle text-dark">{{ account.identifier }}</td>
                        <td class="align-middle">
                            <div class="d-flex align-items-center bg-light rounded px-2 py-1" style="width: fit-content;">
                                <span class="mr-2 text-monospace text-dark" style="min-width: 80px;">
                                    {{ account.showPass ? account.password : '••••••••' }}
                                </span>
                                <button class="btn btn-link btn-sm p-0 text-muted mr-2" @click="$set(account, 'showPass', !account.showPass)" title="Ver/Ocultar">
                                    <i :class="account.showPass ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                                </button>
                                <button class="btn btn-link btn-sm p-0 text-teal" @click="copyToClipboard(account.password)" title="Copiar">
                                    <i class="fas fa-copy"></i>
                                </button>
                            </div>
                        </td>
                        <td class="align-middle text-right pr-4">
                            <button class="btn btn-icon btn-light btn-sm mr-2 text-warning" @click="openModal(account)" title="Editar">
                                <i class="fas fa-pen"></i>
                            </button>
                            <button class="btn btn-icon btn-light btn-sm text-danger" @click="deleteAccount(account.id)" title="Eliminar">
                                <i class="fas fa-trash"></i>
                            </button>
                        </td>
                    </tr>
                    <tr v-if="accounts.length === 0">
                        <td colspan="6" class="text-center py-5 text-muted">
                            <div class="d-flex flex-column align-items-center">
                                <i class="fas fa-folder-open fa-3x mb-3 text-gray-300"></i>
                                <p class="m-0">No tienes cuentas registradas aún.</p>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <user-account-modal ref="modal" :userId="userId" @saved="fetchAccounts"></user-account-modal>
    </div>
</template>

<script>
import request from '@/services/request';
import UserAccountModal from './UserAccountModal.vue';

export default {
    components: { UserAccountModal },
    props: ['userId'],
    data() {
        return {
            accounts: []
        };
    },
    watch: {
        userId: {
            immediate: true,
            handler(val) {
                if (val) this.fetchAccounts();
            }
        }
    },
    methods: {
        async fetchAccounts() {
            try {
                const { data } = await request.get(`/user_accounts/${this.userId}`);
                this.accounts = data.map(acc => ({ ...acc, showPass: false }));
            } catch (error) {
                console.error(error);
            }
        },
        openModal(account = null) {
            this.$refs.modal.open(account);
        },
        async deleteAccount(id) {
            const result = await this.$swal({
                title: '¿Eliminar cuenta?',
                text: "Esta acción no se puede deshacer.",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#ef4444',
                cancelButtonColor: '#6b7280',
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar'
            });

            if (result.isConfirmed) {
                try {
                    await request.delete(`/user_accounts/${id}`);
                    this.fetchAccounts();
                    this.$swal({
                        icon: 'success',
                        title: 'Eliminado',
                        text: 'La cuenta ha sido eliminada.',
                        timer: 2000,
                        showConfirmButton: false
                    });
                } catch (error) {
                    this.$swal('Error!', 'Hubo un problema al eliminar.', 'error');
                }
            }
        },
        copyToClipboard(text) {
            navigator.clipboard.writeText(text).then(() => {
                this.$swal({
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000,
                    icon: 'success',
                    title: 'Copiado al portapapeles'
                });
            });
        }
    }
};
</script>

<style scoped>
/* Colores personalizados */
.text-teal { color: #0f766e !important; }
.bg-teal { background-color: #0f766e !important; }
.btn-teal {
    background-color: #0f766e;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    transition: all 0.2s;
}
.btn-teal:hover {
    background-color: #0d6e66;
    transform: translateY(-1px);
}

.text-gray-800 { color: #1f2937; }
.text-gray-300 { color: #d1d5db; }
.bg-light-gray { background-color: #f9fafb; }

.text-xs { font-size: 0.75rem; }
.border-light { border-color: #e5e7eb !important; }

/* Tabla */
.custom-table th {
    letter-spacing: 0.05em;
}
.custom-table td {
    color: #4b5563;
    font-size: 0.9rem;
}

/* Botones de acción */
.btn-icon {
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.2s;
}
.btn-icon:hover {
    transform: scale(1.1);
}
</style>
